/* global graphql */
import React, { Component } from 'react';
import { Container, Row, Col } from 'react-bs-grid';
import BodyClassName from 'react-body-classname';
import Helmet from 'react-helmet';
import { ChevronUp } from 'react-feather';
import {
  Svg,
  Circle,
  Rect,
  Arc,
  Line,
  SymbolShape,
  Text,
  LineRadial,
  Area,
  AreaRadial,
  Ribbon,
} from '@potion/element';
import Link from 'gatsby-link';
import WindowSize from 'react-window-size';
import get from 'lodash/get';
import { Fab, FabButton } from 'react-fab';

import Space from '../components/Space';
import Title from '../components/Title';
import Page from '../components/Page';
import { COLORS } from '../constants';

class Element extends Component {
  componentDidMount() {
    if (Prism) Prism.highlightAll();
  }

  getSideLength() {
    const { windowWidth } = this.props;
    let sideLength = 340;
    if (windowWidth < 1200) sideLength = 270;
    if (windowWidth < 992) sideLength = 210;
    if (windowWidth < 768) sideLength = 258;
    if (windowWidth < 570) sideLength = windowWidth - 100;
    if (windowWidth === 0) sideLength = 340;
    return sideLength;
  }

  getDocs() {
    const edges = get(this.props, 'data.allMarkdownRemark.edges');
    if (!edges) return '';
    const { html } = edges.filter(edge => edge.node.html.indexOf('name="elements"') > -1)[0].node;
    return html.slice(html.indexOf('<h2>'));
  }

  scrollTo(key) {
    return () => {
      document.querySelector(`[name="elements__${key}"]`).scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    };
  }

  render() {
    const sideLength = this.getSideLength();
    const height = (sideLength / 3) * 2;
    return (
      <BodyClassName className={'no-padding'}>
        <Page fluid name="page-build">
          <Helmet
            title="Potion.js - React + D3"
            meta={[
              {
                property: 'og:url',
                content: 'http://numberpicture.com/build',
              },
              {
                property: 'og:title',
                content: 'Potion.js - React + D3',
              },
              {
                property: 'og:description',
                content: 'Low-level React components for declaratively composing animated, interactive SVG visualizations.',
              },
              {
                name: 'twitter:title',
                content: 'Potion.js - React + D3',
              },
              {
                name: 'twitter:description',
                content: 'Low-level React components for declaratively composing animated, interactive SVG visualizations.',
              },
              {
                charSet: 'utf-8',
              },
            ]}
          />

          <Fab style={{ right: '20px', bottom: '20px' }}>
            <FabButton>
              <a onClick={this.scrollTo('top')}>
                <ChevronUp />
              </a>
            </FabButton>
          </Fab>

          <Container fluid>
            <Row>
              <Container>
                <Row>
                  <Col lg={12}>
                    <div name="elements__top" />
                    <Title>Elements</Title>
                  </Col>
                </Row>
                <Row>
                  <Col lg={8}>
                    <p>Potion Elements are presentational React components that render the building blocks of visualizations. They all render SVG by default but this can be overridden using component injection. The prop names are designed to be as similar to the API of native SVG and D3 generators.</p>
                    <p>Use these in combination with <Link to="/layout">Potion Layouts</Link> and other utilities to construct custom animated  visualizations from the ground up.</p>
                  </Col>
                  <Space half />
                </Row>

                <Row>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('arc')}>
                      <h3>Arc</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Arc
                            innerRadius={0}
                            outerRadius={height / 3}
                            startAngle={Math.PI / 3}
                            endAngle={(Math.PI / 3) * 2}
                            fill={COLORS.DARK_GREY}
                            transform={{
                              translate: [sideLength / 3, height / 2],
                            }}
                          />
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('area')}>
                      <h3>Area</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Area
                            x={d => d.x}
                            y1={d => d.y1}
                            y0={height * 0.8}
                            points={[
                              { x: sideLength * 0.1, y1: height * 0.2 },
                              { x: sideLength * 0.3, y1: height * 0.4 },
                              { x: sideLength * 0.4, y1: height * 0.3 },
                              { x: sideLength * 0.5, y1: height * 0.7 },
                              { x: sideLength * 0.7, y1: height * 0.4 },
                              { x: sideLength * 0.9, y1: height * 0.5 },
                            ]}
                            fill={COLORS.DARK_GREY}
                          />
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('areaRadial')}>
                      <h3>AreaRadial</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <AreaRadial
                            angle={d => d.angle}
                            outerRadius={d => d.outerRadius}
                            innerRadius={height * 0.1}
                            points={[
                              { angle: 0, outerRadius: height * 0.3 },
                              { angle: Math.PI * 0.25, outerRadius: height * 0.2 },
                              { angle: Math.PI * 0.5, outerRadius: height * 0.3 },
                              { angle: Math.PI * 0.75, outerRadius: height * 0.2 },
                              { angle: Math.PI, outerRadius: height * 0.3 },
                              { angle: Math.PI * 1.25, outerRadius: height * 0.2 },
                              { angle: Math.PI * 1.5, outerRadius: height * 0.3 },
                              { angle: Math.PI * 1.75, outerRadius: height * 0.2 },
                              { angle: Math.PI * 2, outerRadius: height * 0.3 },
                            ]}
                            fill={COLORS.DARK_GREY}
                            transform={{
                              translate: [sideLength / 2, height / 2],
                            }}
                          />
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('circle')}>
                      <h3>Circle</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Circle
                            cx={sideLength / 2}
                            cy={height / 2}
                            r={50}
                            fill={COLORS.DARK_GREY}
                          />
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('line')}>
                      <h3>Line</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Line
                            x1={sideLength / 3}
                            y1={height / 3}
                            x2={(sideLength / 3) * 2}
                            y2={(height / 3) * 2}
                            stroke={COLORS.DARK_GREY}
                            strokeWidth={4}
                          />
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('lineRadial')}>
                      <h3>LineRadial</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <LineRadial
                            fill="none"
                            stroke={COLORS.DARK_GREY}
                            points={[
                              [0, 50],
                              [Math.PI * 0.25, 50],
                              [Math.PI * 0.5, 50],
                              [Math.PI * 0.75, 50],
                              [Math.PI, 50],
                              [Math.PI * 1.25, 50],
                              [Math.PI * 1.5, 50],
                              [Math.PI * 1.75, 50],
                              [Math.PI * 2, 50],
                            ]}
                            strokeWidth={4}
                            transform={{
                              translate: [sideLength / 2, height / 2],
                            }}
                          />
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('rect')}>
                      <h3>Rect</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Rect
                            x={sideLength / 3}
                            y={height / 3}
                            width={sideLength / 3}
                            height={height / 3}
                            fill={COLORS.DARK_GREY}
                          />
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('ribbon')}>
                      <h3>Ribbon</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Ribbon
                            source={{
                              startAngle: 0.7524114,
                              endAngle: 1.1212972,
                              radius: height * 0.4,
                            }}
                            target={{
                              startAngle: 1.8617078,
                              endAngle: 1.9842927,
                              radius: height * 0.4,
                            }}
                            fill={COLORS.DARK_GREY}
                            transform={{
                              translate: [sideLength / 3, height / 2],
                            }}
                          />
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('symbolShape')}>
                      <h3>SymbolShape</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <SymbolShape
                            type="symbolCross"
                            fill={COLORS.DARK_GREY}
                            size={2000}
                            transform={{
                              translate: [sideLength / 2, height / 2],
                            }}
                          />
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={4} sm={6}>
                    <a onClick={this.scrollTo('text')}>
                      <h3>Text</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Text
                            dx={sideLength / 2}
                            dy={height / 2}
                            fill={COLORS.DARK_GREY}
                            fontSize={30}
                            textAnchor="middle"
                            alignmentBaseline="middle"
                          >
                            Lorem ipsum
                          </Text>
                        </Svg>
                      </div>
                    </a>
                  </Col>

                </Row>

                <div className="page page-docs">
                  <div className="content">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: this.getDocs(),
                      }}
                    />
                  </div>
                </div>

                <Space />
                <Space />

              </Container>
            </Row>
          </Container>
        </Page>
      </BodyClassName>
    );
  }
}

export default WindowSize(Element);

export const pageQuery = graphql`
  query ElementQuery {
    allMarkdownRemark {
      edges {
        node {
          html
          headings {
            depth
            value
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/pages/element.js